import { Route, Routes } from 'react-router';
import { seedBudgetDefaults } from '../../data/commands/budget';
import { onModuleUsable } from '../../data/moduleHooks';
import { registerTile } from '../../tiles/registry';
import { registerWebModule } from '../registry';
import { BudgetIcon } from './BudgetIcon';
import { copy } from './copy';
import type { WhatsLeftConfig } from './derive';
import { AccountDetailScreen } from './screens/AccountDetailScreen';
import { AccountFormRoute } from './screens/AccountFormRoute';
import { AccountsScreen } from './screens/AccountsScreen';
import { BudgetDashboard } from './screens/BudgetDashboard';
import { ExpectedFormRoute, ReceiveExpectedRoute } from './screens/ExpectedRoutes';
import { OwedScreen, ReceivableFormRoute, ReceiveReceivableRoute } from './screens/OwedRoutes';
import { TransferRoute } from './screens/TransferRoute';
import { BudgetSettingsScreen } from './screens/BudgetSettingsScreen';
import { CashFlowScreen } from './screens/CashFlowScreen';
import { FundDetailScreen } from './screens/FundDetailScreen';
import { FundFormRoute } from './screens/FundFormRoute';
import { FundsScreen } from './screens/FundsScreen';
import { IncomeScreen } from './screens/IncomeScreen';
import { IncomeSourceRoute } from './screens/IncomeSourceRoute';
import { OtherIncomeRoute } from './screens/OtherIncomeRoute';
import { PaydayRoute } from './screens/PaydayRoute';
import { NextPaydayTile } from './tiles/NextPaydayTile';
import { SafeToSpendTile } from './tiles/SafeToSpendTile';
import { WhatsLeftSettings, WhatsLeftTile } from './tiles/WhatsLeftTile';
import { YourMoneyTile } from './tiles/YourMoneyTile';

/** Mounted by v0.1c's ModuleRoute at /m/budget/*, so these paths are relative. */
function BudgetScreen() {
  return (
    <Routes>
      <Route index element={<BudgetDashboard />} />
      <Route path="cashflow" element={<CashFlowScreen />} />
      <Route path="funds" element={<FundsScreen />} />
      <Route path="funds/new" element={<FundFormRoute />} />
      <Route path="funds/:id" element={<FundDetailScreen />} />
      <Route path="funds/:id/edit" element={<FundFormRoute />} />
      <Route path="income" element={<IncomeScreen />} />
      <Route path="income/new" element={<IncomeSourceRoute />} />
      <Route path="income/other" element={<OtherIncomeRoute />} />
      <Route path="income/other/:id" element={<OtherIncomeRoute />} />
      <Route path="income/:id" element={<IncomeSourceRoute />} />
      <Route path="payday/:sourceId/:date" element={<PaydayRoute />} />
      <Route path="settings" element={<BudgetSettingsScreen />} />
      <Route path="accounts" element={<AccountsScreen />} />
      <Route path="accounts/new" element={<AccountFormRoute />} />
      <Route path="accounts/:id" element={<AccountDetailScreen />} />
      <Route path="accounts/:id/edit" element={<AccountFormRoute />} />
      <Route path="transfer" element={<TransferRoute />} />
      <Route path="expected/new" element={<ExpectedFormRoute />} />
      <Route path="expected/:id" element={<ExpectedFormRoute />} />
      <Route path="expected/:id/received" element={<ReceiveExpectedRoute />} />
      <Route path="owed" element={<OwedScreen />} />
      <Route path="owed/new" element={<ReceivableFormRoute />} />
      <Route path="owed/:id/edit" element={<ReceivableFormRoute />} />
      <Route path="owed/:id/receive" element={<ReceiveReceivableRoute />} />
    </Routes>
  );
}

registerWebModule({
  id: 'budget',
  navLabel: copy.moduleName,
  Icon: BudgetIcon,
  Screen: BudgetScreen,
});

registerTile<WhatsLeftConfig>({
  type: 'budget.whats-left',
  moduleId: 'budget',
  title: copy.whatsLeft,
  sizes: ['wide', 'small'],
  defaultSize: 'wide',
  Component: WhatsLeftTile,
  Settings: WhatsLeftSettings,
});

// v0.5 (Ivan): Safe to spend until payday is Budget's main tile on Today, with Your money.
registerTile({
  type: 'budget.safe-to-spend',
  moduleId: 'budget',
  title: copy.safeToSpend,
  sizes: ['wide', 'small'],
  defaultSize: 'wide',
  defaultOnToday: true,
  Component: SafeToSpendTile,
});

registerTile({
  type: 'budget.your-money',
  moduleId: 'budget',
  title: copy.yourMoney,
  sizes: ['wide', 'small'],
  defaultSize: 'wide',
  defaultOnToday: true,
  Component: YourMoneyTile,
});

registerTile({
  type: 'budget.next-payday',
  moduleId: 'budget',
  title: copy.nextPayday,
  sizes: ['small', 'wide'],
  defaultSize: 'small',
  Component: NextPaydayTile,
});

// Runs whenever Budget becomes usable: toggle, admin forced_on, or app start (conventions §5 step 7).
onModuleUsable('budget', seedBudgetDefaults);
